import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, ArrowRight, Sparkles, Globe, Video, X, Loader2, Mail, Copy, CheckCircle2, FileText } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const videoPackages = [
  { id: "video-starter", name: "Starter", tagline: "A steady start on social media.", price: "$200", cadence: "per week", note: "Billed weekly, cancel anytime.",
    features: ["4 custom videos per month (1 new video every week)", "Made for Instagram, Facebook and TikTok", "Captions and simple edits included", "You approve every video before it goes live"],
    recommended: false, testid: "pkg-video-starter" },
  { id: "video-growth", name: "Growth", tagline: "For businesses ready to be seen.", price: "$380", cadence: "per week", note: "Our most popular plan.",
    features: ["8 custom videos per month (2 new videos every week)", "Made for Instagram, Facebook and TikTok", "A dedicated account manager", "Captions and simple edits included", "You approve every video before it goes live"],
    recommended: true, testid: "pkg-video-growth" },
  { id: "video-premium", name: "Premium", tagline: "Full marketing, built around you.", price: "$750", cadence: "per week", note: "For businesses going all in on content.",
    features: ["Monthly performance report", "Videos will be released monthly", "16 videos per month, tailored to your vision", "8 videos focused on quick engagement", "4 videos showcasing your company's personality", "4 videos dedicated to testimonials and Q&A", "Hands-on content planning: directly collaborating with you", "Dedicated account manager for personalized guidance", "Priority support"],
    recommended: false, testid: "pkg-video-premium" },
];

export const websitePackages = [
  { id: "site-foundation", slug: "foundation", name: "Foundation", tagline: "A clean, professional start.", price: "$500", buildFee: 500, secondary: "+ $150 / year hosting", note: "One-time build fee. Changes after launch $49.99/month.",
    features: ["A simple, professional website", "Looks great on phones and computers", "Contact button so customers can reach you", "Yearly plan keeps your site online and up to date"],
    details: ["Up to 3 pages: Home, About and Contact", "Mobile-friendly design that looks great on every screen", "Contact form and click-to-call button", "Your logo, colors and photos built in", "Basic Google setup so people can find you", "Secure hosting and SSL included with the yearly plan"],
    examples: [
      { name: "Grace Bakery", type: "Home bakery", img: "https://images.unsplash.com/photo-1558174685-430919a96c8d?auto=format&fit=crop&w=900&q=80" },
      { name: "Peak Lawn Care", type: "Local service", img: "https://images.pexels.com/photos/221043/pexels-photo-221043.jpeg?auto=compress&w=900" },
    ],
    recommended: false, testid: "pkg-site-foundation" },
  { id: "site-cornerstone", slug: "cornerstone", name: "Cornerstone", tagline: "Tell your full story online.", price: "$1,000", buildFee: 1000, secondary: "+ $150 / year hosting", note: "Our most popular website plan. Changes after launch $49.99/month.",
    features: ["Everything in Foundation", "More pages to tell your full story", "Photo and video galleries", "Social media links and sharing", "Yearly plan keeps your site online and up to date"],
    details: ["Everything in Foundation", "Up to 7 pages: services, gallery, testimonials and more", "Photo and video galleries to show your work", "Social media links and sharing buttons", "Google Maps and business hours", "Search-friendly page titles and descriptions", "Secure hosting and SSL included with the yearly plan"],
    examples: [
      { name: "Harvest Table Catering", type: "Catering company", img: "https://images.unsplash.com/photo-1586880244406-556ebe35f282?auto=format&fit=crop&w=900&q=80" },
      { name: "Faithful Fitness", type: "Personal trainer", img: "https://images.pexels.com/photos/461073/pexels-photo-461073.jpeg?auto=compress&w=900" },
    ],
    recommended: true, testid: "pkg-site-cornerstone" },
  { id: "site-crown", slug: "crown", name: "Crown", tagline: "Our most complete build.", price: "$1,500", buildFee: 1500, secondary: "+ $150 / year hosting", note: "Fully custom around your brand. Changes after launch $49.99/month.",
    features: ["Everything in Cornerstone", "Our most complete website build", "Built around your brand and your goals", "Priority support and faster revisions", "Yearly plan keeps your site online and up to date"],
    details: ["Everything in Cornerstone", "Fully custom design built around your brand", "Unlimited pages planned around your goals", "Booking, online forms or a simple product showcase", "Blog or news section you can grow", "Priority support and faster revisions", "Secure hosting and SSL included with the yearly plan"],
    examples: [
      { name: "Cornerstone Realty Group", type: "Real estate agency", img: "https://images.unsplash.com/photo-1542744095-291d1f67b221?auto=format&fit=crop&w=900&q=80" },
      { name: "Bloom & Co. Boutique", type: "Retail boutique", img: "https://images.unsplash.com/photo-1702047143580-0f349be86369?auto=format&fit=crop&w=900&q=80" },
    ],
    recommended: false, testid: "pkg-site-crown" },
];

export default function MarketingPackages() {
  const [tab, setTab] = useState("videos");
  const [buyPkg, setBuyPkg] = useState(null);

  return (
    <section id="packages" className="scroll-mt-24 py-20">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center">
          <p className="kicker">Packages</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Simple, honest pricing</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Every package is approved by you before anything goes live. No lock-ins. No surprises.</p>
        </div>

        <div className="mt-10 flex justify-center">
          <div className="inline-flex p-1.5 bg-white rounded-full border border-[color:var(--dms-navy)]/10 shadow-sm" data-testid="pkg-tabs">
            <button onClick={() => setTab("videos")} data-testid="pkg-tab-videos"
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition ${tab === "videos" ? "bg-[color:var(--dms-navy)] text-white shadow" : "text-[color:var(--dms-navy)]/70 hover:text-[color:var(--dms-navy)]"}`}>
              <Video className="w-4 h-4" /> Video marketing
            </button>
            <button onClick={() => setTab("websites")} data-testid="pkg-tab-websites"
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition ${tab === "websites" ? "bg-[color:var(--dms-navy)] text-white shadow" : "text-[color:var(--dms-navy)]/70 hover:text-[color:var(--dms-navy)]"}`}>
              <Globe className="w-4 h-4" /> Websites
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-[color:var(--dms-navy)]/60 font-serif italic">
          {tab === "videos" ? "Videos made for your social media. You approve every one before it goes live." : "One-time build fee, plus $150 a year to keep it online. Changes after launch are $49.99 a month."}
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {(tab === "videos" ? videoPackages : websitePackages).map((p) => (
            <PackageCard key={p.name} pkg={p} onBuy={() => setBuyPkg(p)} />
          ))}
        </div>

        <div className="mt-14 bg-[color:var(--dms-cream-2)]/70 border border-[color:var(--dms-navy)]/10 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
          <div className="w-12 h-12 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[color:var(--dms-navy)]" />
          </div>
          <div className="flex-1">
            <p className="kicker">Need something different?</p>
            <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-2">We build custom packages too</h3>
            <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">Tell us your goals and we'll put a plan together just for you.</p>
          </div>
          <a href="#intake" className="btn-peach" data-testid="pkg-custom-cta">Plan a custom package <ArrowRight className="w-4 h-4" /></a>
        </div>
      </div>

      {buyPkg && <BuyModal pkg={buyPkg} onClose={() => setBuyPkg(null)} />}
    </section>
  );
}

function PackageCard({ pkg, onBuy }) {
  const isHighlight = pkg.recommended;
  const btnCls = isHighlight ? "bg-[color:var(--dms-peach)] text-[color:var(--dms-navy)] hover:bg-[color:var(--dms-peach-light)]" : "bg-[color:var(--dms-navy)] text-white hover:bg-[color:var(--dms-navy)]/90";
  return (
    <article data-testid={pkg.testid}
      className={`relative rounded-3xl p-8 flex flex-col ${isHighlight ? "bg-[color:var(--dms-navy)] text-white shadow-[0_30px_60px_-20px_rgba(20,36,61,0.45)] md:-translate-y-3" : "bg-white border border-[color:var(--dms-navy)]/10"}`}>
      {isHighlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[color:var(--dms-peach)] text-[color:var(--dms-navy)] text-[10px] tracking-widest uppercase font-semibold shadow">
          <Sparkles className="w-3 h-3" /> Most popular
        </span>
      )}
      <h3 className={`font-serif text-2xl ${isHighlight ? "text-white" : "text-[color:var(--dms-navy)]"}`}>{pkg.name}</h3>
      <p className={`mt-2 text-sm ${isHighlight ? "text-white/70" : "text-[color:var(--dms-navy)]/60"}`}>{pkg.tagline}</p>

      <div className="mt-6 flex items-baseline gap-2">
        <span className={`font-serif text-4xl md:text-5xl ${isHighlight ? "text-white" : "text-[color:var(--dms-navy)]"}`}>{pkg.price}</span>
        <span className={`text-sm ${isHighlight ? "text-white/60" : "text-[color:var(--dms-navy)]/55"}`}>{pkg.cadence || ""}</span>
      </div>
      {pkg.secondary && <p className="mt-1 text-sm text-[color:var(--dms-peach)] font-semibold">{pkg.secondary}</p>}
      <p className={`mt-2 text-xs ${isHighlight ? "text-white/55" : "text-[color:var(--dms-navy)]/50"}`}>{pkg.note}</p>

      <div className={`my-7 h-px ${isHighlight ? "bg-white/15" : "bg-[color:var(--dms-navy)]/10"}`} />

      <ul className="space-y-3 flex-1">
        {pkg.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isHighlight ? "bg-[color:var(--dms-peach)]" : "bg-[color:var(--dms-peach-light)]/70"}`}>
              <Check className="w-3 h-3 text-[color:var(--dms-navy)]" />
            </span>
            <span className={`text-sm leading-relaxed ${isHighlight ? "text-white/90" : "text-[color:var(--dms-navy)]/80"}`}>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-7 space-y-2">
        {pkg.slug ? (
          <Link to={`/websites/${pkg.slug}`} data-testid={`${pkg.testid}-see-more`} className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold tracking-wide transition ${btnCls}`}>
            See more <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <button type="button" onClick={onBuy} data-testid={`${pkg.testid}-buy`} className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold tracking-wide transition ${btnCls}`}>
            <FileText className="w-4 h-4" /> Request invoice
          </button>
        )}
        <a href="#intake" data-testid={`${pkg.testid}-quote`}
          className={`w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-xs tracking-widest uppercase font-semibold transition ${isHighlight ? "text-white/70 hover:text-white" : "text-[color:var(--dms-navy)]/60 hover:text-[color:var(--dms-navy)]"}`}>
          or request a quote <ArrowRight className="w-3 h-3" />
        </a>
      </div>
    </article>
  );
}

export function BuyModal({ pkg, onClose }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ customer_name: "", customer_email: "", business_name: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/packages/request`, {
        package_id: pkg.id,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        business_name: form.business_name || null,
        phone: form.phone || null,
        notes: form.notes || null,
      });
      setResult(data);
      toast({ title: "Invoice sent", description: "Check your email for payment details." });
    } catch (err) {
      toast({ title: "Could not send invoice", description: "Please try again or request a quote.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(result.zelle_email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 bg-[color:var(--dms-navy)]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="pkg-buy-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[color:var(--dms-cream)] flex items-center justify-center hover:bg-[color:var(--dms-navy)]/10 z-10" data-testid="pkg-buy-close">
          <X className="w-4 h-4 text-[color:var(--dms-navy)]" />
        </button>

        {result ? (
          <div className="p-7 text-center" data-testid="pkg-buy-success">
            <div className="w-14 h-14 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[color:var(--dms-navy)]" />
            </div>
            <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-5">Invoice sent</h3>
            <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">We emailed your invoice to <b>{form.customer_email}</b>. Pay via Zelle to confirm your order.</p>

            <div className="mt-6 bg-[color:var(--dms-cream-2)]/60 border border-dashed border-[color:var(--dms-peach)] rounded-2xl p-5 text-left">
              <p className="kicker">Zelle payment</p>
              <div className="mt-3 flex items-baseline justify-between">
                <span className="text-xs text-[color:var(--dms-navy)]/60">{result.weekly ? "Full month (4 weeks)" : "Amount due"}</span>
                <span className="font-serif text-2xl text-[color:var(--dms-navy)]" data-testid="pkg-amount-due">${(result.weekly ? result.monthly_amount : result.amount).toFixed(2)}</span>
              </div>
              {result.weekly && (
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-xs text-[color:var(--dms-navy)]/60">Or 4 weekly instalments</span>
                  <span className="font-serif text-lg text-[color:var(--dms-navy)]" data-testid="pkg-weekly-amount">${result.amount.toFixed(2)} / week</span>
                </div>
              )}
              <div className="mt-2 flex items-baseline justify-between">
                <span className="text-xs text-[color:var(--dms-navy)]/60">Send to</span>
                <button onClick={copyEmail} className="font-mono text-sm text-[color:var(--dms-navy)] inline-flex items-center gap-2 hover:text-[color:var(--dms-peach)]" data-testid="pkg-copy-zelle">
                  {result.zelle_email}
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--dms-peach)]" /> : <Copy className="w-3.5 h-3.5 opacity-50" />}
                </button>
              </div>
              <div className="mt-2 flex items-baseline justify-between">
                <span className="text-xs text-[color:var(--dms-navy)]/60">Memo</span>
                <span className="font-mono text-sm text-[color:var(--dms-navy)]"><b>{result.order_number}</b></span>
              </div>
            </div>

            <p className="mt-5 text-[11px] text-[color:var(--dms-navy)]/55">{result.weekly && "You're responsible for each weekly payment: pay the whole month at once or in 4 weekly instalments. "}We'll confirm by email as soon as your payment arrives, and reach out personally within one business day.</p>
            <button onClick={onClose} className="btn-peach w-full justify-center mt-6" data-testid="pkg-buy-done">Got it <ArrowRight className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="p-7">
            <p className="kicker">Request invoice</p>
            <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-2">{pkg.name}</h3>
            <p className="text-sm text-[color:var(--dms-navy)]/65 mt-1">{pkg.tagline}</p>

            <div className="mt-5 flex items-baseline gap-2 border-b border-[color:var(--dms-navy)]/10 pb-5">
              <span className="font-serif text-3xl text-[color:var(--dms-navy)]">{pkg.price}</span>
              <span className="text-sm text-[color:var(--dms-navy)]/60">{pkg.cadence}</span>
              {pkg.secondary && <span className="text-xs text-[color:var(--dms-peach)] font-semibold ml-1">{pkg.secondary}</span>}
            </div>

            <form onSubmit={submit} className="mt-5 space-y-4">
              <ModalField label="Your name *" testid="pkg-buy-name">
                <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required className="mfield" placeholder="Jane Smith" />
              </ModalField>
              <ModalField label="Email for invoice *" testid="pkg-buy-email">
                <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} required className="mfield" placeholder="jane@yourbusiness.com" />
              </ModalField>
              <div className="grid grid-cols-2 gap-3">
                <ModalField label="Business" testid="pkg-buy-business">
                  <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="mfield" placeholder="Jane's Co." />
                </ModalField>
                <ModalField label="Phone" testid="pkg-buy-phone">
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mfield" placeholder="(555) 555-5555" />
                </ModalField>
              </div>
              <ModalField label="Anything else?" testid="pkg-buy-notes">
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mfield" placeholder="Deadline, style, special requests…" />
              </ModalField>

              <button type="submit" disabled={submitting} className="btn-peach w-full justify-center" data-testid="pkg-buy-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending invoice…</> : <><Mail className="w-4 h-4" /> Email me the invoice</>}
              </button>
              <p className="text-[11px] text-[color:var(--dms-navy)]/50 text-center">Pay by Zelle. We'll email instructions and get started once payment arrives.</p>
            </form>

            <style>{`
              .mfield { width: 100%; border: 1px solid rgba(26,47,77,0.18); background: #fff; padding: 11px 14px; border-radius: 12px; font-size: 14px; color: #1a2f4d; outline: none; transition: border-color .2s; font-family: 'Inter', sans-serif; }
              .mfield:focus { border-color: var(--dms-peach); box-shadow: 0 0 0 3px rgba(212,165,116,0.15); }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
}

function ModalField({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
